import { FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import classnames from 'classnames';
import React, { MouseEvent, Ref, useEffect, useImperativeHandle, useState } from 'react';

import { CrossIcon } from '@icons';

import styles from './Dropdown.module.scss';

type OnChangeProps =
  | {
      /**
       * If true, the onChange call back will receive a
       * string instead of a ChangeEvent
       */
      provideValueOnChange: true;
      onChange?: (value: string) => void;
    }
  | {
      provideValueOnChange?: false;
      onChange?: (event: SelectChangeEvent<string>) => void;
    };

export interface DropdownOption {
  label: string;
  value: string;
}

export type DropdownProps = {
  label?: string;
  defaultValue?: string;
  options: DropdownOption[];
  name?: string;
  value?: string;
  placeholder?: string;
  className?: string;
  testId?: string;
  disabled?: boolean;
  ref?: Ref<DropdownRef>;
} & OnChangeProps;

export type DropdownRef = { reset: () => void };

export const Dropdown = ({
  label,
  options,
  name,
  defaultValue = '',
  onChange,
  provideValueOnChange,
  placeholder,
  value,
  className,
  testId,
  disabled,
  ref,
}: DropdownProps) => {
  const [localValue, setLocalValue] = useState(defaultValue);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
    if (provideValueOnChange) onChange?.(newValue);
    else {
      onChange?.(event);
    }
  };

  const handleReset = () => {
    setLocalValue(defaultValue);
  };

  const handleClear = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleChange({
      target: { value: defaultValue },
    } as SelectChangeEvent<string>);
  };

  useImperativeHandle(ref, () => ({ reset: handleReset }), []);

  useEffect(() => {
    if (typeof value === 'string') {
      setLocalValue(value);
    }
  }, [value]);

  if (!options) return null;

  return (
    <>
      {label && <div className={styles.label}>{label}</div>}
      <FormControl className={classnames(className, styles.formControl)}>
        <Select
          sx={{
            fontSize: '18px',
          }}
          name={name}
          value={localValue}
          placeholder={placeholder}
          data-testid={testId}
          displayEmpty
          onChange={handleChange}
          defaultValue={defaultValue}
          aria-label={name}
          disabled={disabled}
          renderValue={(value) => (
            <div className={styles.placeholder}>
              {options.find((option) => option.value === value)?.label || placeholder}
            </div>
          )}>
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              className={styles.menuItem}
              sx={{
                fontSize: '18px',
                paddingTop: '0px',
              }}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {!!localValue.length && (
          <button onClick={handleClear}>
            <CrossIcon size={16} />
          </button>
        )}
      </FormControl>
    </>
  );
};
